import { useEffect, useState, type CSSProperties, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  WEEKLY_NOTE_SECTIONS, WEEKLY_ROLE_LABELS, WEEKLY_STATUS_LABELS, isBlankRich, toRichHtml,
  type WeeklyEntry, type WeeklyNotes, type WeeklyRole, type WeeklyStatus,
} from '@dxtech/shared';
import { weeklyApi, type WeeklyPerson } from '../api/weekly.js';
import { useApp } from '../state/app.js';
import { useIsMobile } from '../lib/useIsMobile.js';
import { Avatar, ConfirmDialog, ModalFrame, Spinner } from '../ui/common.js';
import { RichTextEditor } from '../ui/RichText.js';
import { ArrowDownRegular, ArrowLeftRegular, ArrowUpRegular, AttachRegular, DismissRegular } from '@fluentui/react-icons';
import type { WeeklyFile } from '@dxtech/shared';

// KST 표기 — "10/26(월) 10:00"
const kstParts = (iso: string) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return p as Record<string, string>;
};
const fmtShort = (iso: string) => { const p = kstParts(iso); return `${+p.month}/${+p.day}(${p.weekday}) ${p.hour}:${p.minute}`; };
const fmtFull = (iso: string) => { const p = kstParts(iso); return `${p.year}.${p.month}.${p.day}(${p.weekday}) ${p.hour}:${p.minute}`; };
/** datetime-local 값(KST 기준) ↔ ISO */
const toLocalInput = (iso: string) => { const p = kstParts(iso); return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`; };
const fromLocalInput = (v: string) => new Date(`${v}:00+09:00`).toISOString();
/** 연중 주차(그 해 첫 월요일 주 = 1주차) */
const weekNo = (key: string) => {
  const d = Date.parse(`${key}T00:00:00Z`);
  const jan1 = new Date(Date.UTC(+key.slice(0, 4), 0, 1));
  const firstMon = jan1.getTime() + ((8 - jan1.getUTCDay()) % 7) * 86400000;
  return Math.floor((d - firstMon) / (7 * 86400000)) + 1;
};

const STATUS_STYLE: Record<WeeklyStatus, CSSProperties> = {
  upcoming: { background: '#F3F4F6', color: '#6B7280' },
  writing: { background: '#DBEAFE', color: '#1D4ED8' },
  closed: { background: '#DCFCE7', color: '#15803D' },
};
const Badge = ({ status }: { status: WeeklyStatus }) => (
  <span style={{ ...STATUS_STYLE[status], fontSize: 11.5, fontWeight: 700, padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>{WEEKLY_STATUS_LABELS[status]}</span>
);

// 주간보고 — 금년 회의 목록(최신순) → 회의별 전 임직원 공유 화면
export function Weekly(): JSX.Element {
  const { me } = useApp();
  const isAdmin = me.permissions.canManageMembers;
  const [key, setKey] = useState<string | null>(null);
  const [orderOpen, setOrderOpen] = useState(false);
  const [year, setYear] = useState<string | undefined>(undefined); // 미선택 = 올해(서버 기준)
  const list = useQuery({ queryKey: ['weekly', 'list', year ?? 'current'], queryFn: () => weeklyApi.list(year) });

  if (key) return <WeekView weekKey={key} isAdmin={isAdmin} onBack={() => setKey(null)} />;

  const weeks = list.data?.weeks ?? [];
  return (
    <div style={{ maxWidth: 880, margin: '0 auto', animation: 'dxfade .3s ease', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* 화면 제목은 상단바에 있으므로 회의 규칙만 한 줄로 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: '#6B7280' }}>
          회의는 매주 월요일 10시이며, 직전 회의 당일 오후 1시부터 회의 시작 전까지 작성합니다.
        </div>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {(list.data?.years?.length ?? 0) > 0 && (
            <select className="dx-select" aria-label="연도" value={list.data?.year ?? ''} onChange={(e) => setYear(e.target.value)} style={{ height: 34 }}>
              {list.data!.years.map((y) => <option key={y} value={y}>{y}년</option>)}
            </select>
          )}
          {isAdmin && <button className="dx-btn dx-btn-ghost" style={{ fontSize: 12.5 }} onClick={() => setOrderOpen(true)}>임직원 구분·표시 순서 설정</button>}
        </span>
      </div>

      <div className="dx-card" style={{ overflow: 'hidden' }}>
        {list.isLoading ? (
          <div style={{ padding: 32, textAlign: 'center' }}><Spinner size={22} /></div>
        ) : list.error ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#DC2626', fontSize: 13 }}>{(list.error as Error).message}</div>
        ) : weeks.map((w) => (
          <button key={w.key} onClick={() => setKey(w.key)}
            style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', border: 'none', borderBottom: '1px solid #F3F4F6', background: w.key === list.data?.next ? '#F8FBFF' : '#fff', padding: '14px 18px', textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer' }}>
            <div style={{ width: 54, textAlign: 'center', flexShrink: 0 }}>
              <div style={{ fontSize: 11, color: '#9CA3AF' }}>{w.key.slice(0, 4)}</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#1F2937' }}>{weekNo(w.key)}주차</div>
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{fmtFull(w.meetingAt)} 주간회의{w.key === list.data?.next && <span style={{ marginLeft: 8, fontSize: 11.5, color: '#2563EB' }}>다음 회의</span>}</div>
              <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>작성 기간 {fmtShort(w.openAt)} ~ {fmtShort(w.meetingAt)}</div>
            </div>
            <span style={{ fontSize: 12.5, color: '#6B7280', whiteSpace: 'nowrap' }}>작성 {w.submitted}/{list.data?.total ?? 0}</span>
            <Badge status={w.status} />
          </button>
        ))}
      </div>

      {orderOpen && <OrderModal onClose={() => setOrderOpen(false)} />}
    </div>
  );
}

/** 서식 텍스트 표시(예전 순수 텍스트도 줄바꿈 유지) — 저장 시·표시 시 모두 허용목록 정제 */
const Rich = ({ html, muted }: { html: string; muted?: boolean }) => (isBlankRich(html)
  ? <span style={{ color: '#C0C4CB', fontSize: 13 }}>미작성</span>
  : <div className="dx-rte-view" style={{ fontSize: 13, lineHeight: 1.65, wordBreak: 'break-word', color: muted ? '#4B5563' : '#1F2937' }} dangerouslySetInnerHTML={{ __html: toRichHtml(html) }} />);
const prevBox: CSSProperties = { background: '#F9FAFB', border: '1px solid #F1F3F5', borderRadius: 8, padding: '8px 10px', minHeight: 40 };

function WeekView({ weekKey, isAdmin, onBack }: { weekKey: string; isAdmin: boolean; onBack: () => void }): JSX.Element {
  const { me, flash } = useApp();
  const qc = useQueryClient();
  const mobile = useIsMobile();
  const q = useQuery({ queryKey: ['weekly', weekKey], queryFn: () => weeklyApi.week(weekKey) });
  const d = q.data;
  const mine = d?.entries.find((e) => e.upn.toLowerCase() === me.upn.toLowerCase());
  const [done, setDone] = useState('');
  const [plan, setPlan] = useState('');
  const [notes, setNotes] = useState<WeeklyNotes['sections'] | null>(null);
  const [saving, setSaving] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [fileBusy, setFileBusy] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<{ target: 'entry' | 'notes'; file: WeeklyFile; upn?: string } | null>(null);
  const [adminEdit, setAdminEdit] = useState<string | null>(null); // 관리자가 수정 중인 보고자 upn
  // 편집 대상 — 관리자가 고른 행, 아니면 작성 기간의 내 행
  const target = adminEdit ? d?.entries.find((e) => e.upn === adminEdit) : d?.editable ? mine : undefined;
  useEffect(() => { if (target) { setDone(target.done); setPlan(target.plan); } }, [target?.upn, target?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (d) setNotes(d.notes.sections); }, [d?.notes.updatedAt, d?.key]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirtyMine = !!target && (done !== target.done || plan !== target.plan);
  const dirtyNotes = !!d && !!notes && JSON.stringify(notes) !== JSON.stringify(d.notes.sections);
  const dirty = dirtyMine || dirtyNotes;

  const run = async (fn: () => Promise<unknown>, okMsg: string) => {
    setSaving(true);
    try { await fn(); flash(okMsg); await qc.invalidateQueries({ queryKey: ['weekly'] }); }
    catch (e) { flash(e instanceof Error ? e.message : '저장 실패'); } finally { setSaving(false); }
  };
  const save = () => run(async () => {
    await weeklyApi.save(weekKey, done, plan, adminEdit && adminEdit !== mine?.upn ? adminEdit : undefined);
    setAdminEdit(null);
  }, '주간보고를 저장했습니다');
  const saveNotes = () => run(() => weeklyApi.saveNotes(weekKey, notes!, d?.notes.updatedAt), '업무진행/공지사항을 저장했습니다');

  if (q.isLoading || !d) {
    return <div style={{ padding: 40, textAlign: 'center' }}>{q.error ? <span style={{ color: '#DC2626', fontSize: 13 }}>{(q.error as Error).message}</span> : <Spinner size={24} />}</div>;
  }

  const cols = mobile ? '1fr' : '172px repeat(3, minmax(0, 1fr))';
  const head = (t: string, sub?: string) => (
    <div style={{ fontSize: 12, fontWeight: 700, color: '#4B5563' }}>{t}{sub && <span style={{ fontWeight: 500, color: '#9CA3AF', marginLeft: 4 }}>{sub}</span>}</div>
  );
  const headRow = (labels: [string, string, string, string]) => (!mobile && (
    <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 14, padding: '10px 18px', background: '#F9FAFB', borderBottom: '1px solid #EEF0F3' }}>
      {head(labels[0])}{head(labels[1])}{head(labels[2])}{head(labels[3])}
    </div>
  ));
  const cellLabel = (t: string) => (mobile ? <div style={{ fontSize: 11.5, fontWeight: 700, color: '#9CA3AF', marginBottom: 4 }}>{t}</div> : null);
  const saveBar = (isDirty: boolean, onSave: () => void, label = '저장') => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, justifyContent: 'flex-end' }}>
      {isDirty && <span style={{ fontSize: 12, color: '#D97706' }}>저장하지 않은 변경사항</span>}
      <button className="dx-btn dx-btn-primary" style={{ fontSize: 12.5, padding: '7px 16px' }} disabled={saving || !isDirty} onClick={onSave}>{saving ? '저장 중…' : label}</button>
    </div>
  );

  // 첨부 — 올리기·빼기는 편집 권한이 있을 때만, 내려받기는 전원
  const addFiles = async (target: 'entry' | 'notes', section: string, files: File[], upn?: string) => {
    try {
      for (const f of files) {
        if (f.size > 50 * 1024 * 1024) { flash(`${f.name}: 50MB 초과로 제외`); continue; }
        setFileBusy(`${target}-${section}-${upn ?? ''}`);
        await weeklyApi.uploadFile(weekKey, target, section, f, upn);
      }
      await qc.invalidateQueries({ queryKey: ['weekly', weekKey] });
    } catch (err) { flash(err instanceof Error ? err.message : '첨부 실패'); } finally { setFileBusy(null); }
  };
  const attach = (target: 'entry' | 'notes', section: string, all: WeeklyFile[] | undefined, editable: boolean, style?: CSSProperties, upn?: string) => (
    <Attachments files={(all ?? []).filter((f) => f.section === section)} editable={editable} style={style}
      busy={fileBusy === `${target}-${section}-${upn ?? ''}`}
      onAdd={(fs) => void addFiles(target, section, fs, upn)}
      onRemove={(file) => setPendingRemove({ target, file, upn })}
      onOpen={(file) => void weeklyApi.downloadFile(file.id, file.name).catch((err) => flash(err instanceof Error ? err.message : '내려받기 실패'))} />
  );

  const row = (e: WeeklyEntry) => {
    const isMe = e.upn === mine?.upn;
    const edit = target?.upn === e.upn;
    const byAdmin = edit && !!adminEdit;
    return (
      <div key={e.upn} id={isMe ? 'weekly-me' : undefined}
        style={{ display: 'grid', gridTemplateColumns: cols, gap: mobile ? 10 : 14, padding: '14px 18px', borderBottom: '1px solid #F3F4F6', background: byAdmin ? '#FFFBEB' : isMe ? '#F8FBFF' : '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
          <Avatar name={e.name} upn={e.upn} size={36} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{e.name}{isMe && <span style={{ marginLeft: 6, fontSize: 11, color: '#2563EB' }}>나</span>}</div>
            <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>{[e.dept, e.title].filter(Boolean).join(' · ')}</div>
            {e.updatedAt && <div style={{ fontSize: 11, color: '#C0C4CB', marginTop: 4 }}>{fmtShort(e.updatedAt)} 저장</div>}
            {e.editedByName && <div style={{ fontSize: 11, color: '#B45309', marginTop: 2 }}>관리자 수정 · {e.editedByName}</div>}
            {/* 관리자 — 모든 보고자 칸을 기간 제한 없이 수정 */}
            {d.canEditAll && !edit && (
              <button className="dx-btn dx-btn-ghost" style={{ marginTop: 6, padding: '3px 9px', fontSize: 11.5 }}
                onClick={() => { if (!dirtyMine) setAdminEdit(e.upn); else flash('수정 중인 내용을 먼저 저장하거나 취소하세요'); }}>수정</button>
            )}
            {byAdmin && (
              <button className="dx-btn dx-btn-ghost" style={{ marginTop: 6, padding: '3px 9px', fontSize: 11.5 }} onClick={() => setAdminEdit(null)}>수정 취소</button>
            )}
          </div>
        </div>
        <div>{cellLabel('전주 진행 예정')}<div style={prevBox}><Rich html={e.prevPlan} muted /></div></div>
        <div>
          {cellLabel('전주 진행 내용')}
          {edit ? <RichTextEditor key={`d-${e.upn}`} compact rich value={done} onChange={setDone} minHeight={150} placeholder="지난주에 진행한 업무 (이미지는 복사 후 Ctrl+V)" /> : <Rich html={e.done} />}
          {attach('entry', 'done', e.files, edit, undefined, byAdmin ? e.upn : undefined)}
        </div>
        <div>
          {cellLabel('금주 진행 예정')}
          {edit ? <RichTextEditor key={`p-${e.upn}`} compact rich value={plan} onChange={setPlan} minHeight={150} placeholder="이번 주 진행할 업무 (다음 주 '전주 진행 예정'으로 표시)" /> : <Rich html={e.plan} />}
          {attach('entry', 'plan', e.files, edit, undefined, byAdmin ? e.upn : undefined)}
          {edit && saveBar(dirtyMine, () => void save(), byAdmin ? `${e.name} 님 칸 저장` : '저장')}
        </div>
      </div>
    );
  };

  // 피보고자 메모 — 기존 주간회의 문서 하단(업무진행·공지사항·참고용), 구분별 내용 1개.
  // 이번 주 저장분이 없으면 최근 저장된 주의 내용을 그대로 보여주고, 수정·저장하면 이번 주 것이 됨. 비어 있고 작성 권한도 없으면 숨김
  const notesVisible = d.canEditNotes || (d.notes.files?.length ?? 0) > 0 || WEEKLY_NOTE_SECTIONS.some((s) => !isBlankRich(d.notes.sections[s.key]));
  const noteCols = mobile ? '1fr' : '172px minmax(0, 1fr)';
  const noteRow = (s: (typeof WEEKLY_NOTE_SECTIONS)[number], i: number) => (
    <div key={s.key} style={{ display: 'grid', gridTemplateColumns: noteCols, gap: mobile ? 8 : 14, padding: '14px 18px', borderBottom: i === WEEKLY_NOTE_SECTIONS.length - 1 ? 'none' : '1px solid #F3F4F6' }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{s.label}</div>
        <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>{s.hint}</div>
      </div>
      {d.canEditNotes
        ? <RichTextEditor key={`${weekKey}-${s.key}-${d.notes.updatedAt ?? ''}`} compact rich value={notes?.[s.key] ?? d.notes.sections[s.key]} minHeight={110}
            onChange={(v) => setNotes((n) => (n ? { ...n, [s.key]: v } : n))} placeholder={`${s.label} 내용`} />
        : <Rich html={d.notes.sections[s.key]} />}
      {attach('notes', s.key, d.notes.files, d.canEditNotes, mobile ? undefined : { gridColumn: 2 })}
    </div>
  );
  const inherited = !!d.notesFrom;

  const notice = d.canWrite
    ? d.status === 'writing' ? `${fmtShort(d.meetingAt)} 회의 시작 전까지 본인 칸을 작성·수정할 수 있습니다.`
      : d.status === 'upcoming' ? `${fmtShort(d.openAt)}부터 작성할 수 있습니다.` : '회의가 진행되어 수정할 수 없습니다.'
    : d.canEditNotes ? '' // 피보고자 — 안내 문구 없음
      : '보고 대상이 아닙니다. 조회만 가능합니다.';
  const highlight = d.editable || d.canEditNotes;

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', animation: 'dxfade .3s ease', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button className="dx-btn dx-btn-ghost" style={{ padding: '6px 10px', fontSize: 13 }} onClick={() => (dirty ? setLeaving(true) : onBack())}>
          <ArrowLeftRegular aria-hidden style={{ fontSize: 16 }} />목록
        </button>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>{weekNo(weekKey)}주차 주간보고 <span style={{ fontWeight: 600, color: '#6B7280', fontSize: 15 }}>{fmtFull(d.meetingAt)}</span></h2>
          <div style={{ fontSize: 12.5, color: '#9CA3AF', marginTop: 2 }}>
            <span>작성 {d.submitted}/{d.total}명</span>
            <span style={{ marginLeft: 12 }}>작성 기간 {fmtShort(d.openAt)} ~ {fmtShort(d.meetingAt)}</span>
          </div>
        </div>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Badge status={d.status} />
          {mine && <button className="dx-btn dx-btn-soft" style={{ fontSize: 12.5 }} onClick={() => document.getElementById('weekly-me')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>내 칸으로</button>}
          {d.canEditNotes && <button className="dx-btn dx-btn-soft" style={{ fontSize: 12.5 }} onClick={() => document.getElementById('weekly-notes')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>업무진행/공지사항</button>}
          {isAdmin && d.status !== 'closed' && <button className="dx-btn dx-btn-ghost" style={{ fontSize: 12.5 }} onClick={() => setMeetingOpen(true)}>회의 일시 변경</button>}
        </span>
      </div>

      {notice && <div style={{ fontSize: 12.5, color: highlight ? '#1D4ED8' : '#6B7280', background: highlight ? '#EFF6FF' : '#F9FAFB', borderRadius: 8, padding: '8px 12px' }}>{notice}</div>}

      <div className="dx-card" style={{ overflow: 'hidden' }}>
        {headRow(['담당자', '전주 진행 예정', '전주 진행 내용', '금주 진행 예정'])}
        {d.entries.length ? d.entries.map(row) : <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>보고자가 없습니다.</div>}
      </div>

      {notesVisible && (
        <div id="weekly-notes" className="dx-card" style={{ overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid #EEF0F3', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>업무진행/공지사항</div>
            {inherited
              ? <span style={{ fontSize: 11.5, fontWeight: 700, color: '#B45309', background: '#FEF3C7', borderRadius: 999, padding: '3px 9px' }}>{weekNo(d.notesFrom!)}주차 내용 · 이번 주 미저장</span>
              : <span style={{ fontSize: 12, color: '#9CA3AF' }}>피보고자 작성{d.notes.updatedAt ? ` · ${d.notes.updatedByName ?? ''} ${fmtShort(d.notes.updatedAt)} 수정` : ''}</span>}
          </div>
          {WEEKLY_NOTE_SECTIONS.map(noteRow)}
          {d.canEditNotes && (
            <div style={{ padding: '0 18px 14px' }}>
              {/* 이어받은 내용은 수정 없이도 이번 주 것으로 저장할 수 있게 */}
              {saveBar(dirtyNotes || inherited, () => void saveNotes(), inherited && !dirtyNotes ? '이번 주로 저장' : '저장')}
            </div>
          )}
        </div>
      )}

      {leaving && (
        <ConfirmDialog title="저장하지 않은 변경사항" message="작성 중인 내용을 저장하지 않고 목록으로 돌아갈까요?" confirmLabel="저장 안 하고 나가기" danger
          onConfirm={() => { setLeaving(false); onBack(); }} onCancel={() => setLeaving(false)} />
      )}
      {meetingOpen && <MeetingModal weekKey={weekKey} current={d.meetingAt} onClose={() => setMeetingOpen(false)} />}
      {pendingRemove && (
        <ConfirmDialog title="첨부 빼기" danger confirmLabel="빼기"
          message={`'${pendingRemove.file.name}' 첨부를 뺄까요?${pendingRemove.target === 'entry' ? ' 파일도 함께 삭제됩니다.' : ''}`}
          onConfirm={() => {
            const r = pendingRemove; setPendingRemove(null);
            void weeklyApi.removeFile(weekKey, r.target, r.file.id, r.upn)
              .then(() => qc.invalidateQueries({ queryKey: ['weekly', weekKey] }))
              .catch((err) => flash(err instanceof Error ? err.message : '첨부 빼기 실패'));
          }}
          onCancel={() => setPendingRemove(null)} />
      )}
    </div>
  );
}

/** 첨부 목록 — 파일 이름을 누르면 내려받기, 편집 중이면 '파일 첨부'·빼기 */
function Attachments({ files, editable, busy, style, onAdd, onRemove, onOpen }: {
  files: WeeklyFile[]; editable: boolean; busy: boolean; style?: CSSProperties;
  onAdd: (files: File[]) => void; onRemove: (f: WeeklyFile) => void; onOpen: (f: WeeklyFile) => void;
}): JSX.Element | null {
  if (!files.length && !editable) return null;
  const size = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.ceil(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, alignItems: 'center', ...style }}>
      {files.map((f) => (
        <span key={f.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%', border: '1px solid #E5E7EB', borderRadius: 8, background: '#fff', padding: '3px 4px 3px 8px', fontSize: 12 }}>
          <AttachRegular aria-hidden style={{ fontSize: 13, color: '#6B7280', flexShrink: 0 }} />
          <button onClick={() => onOpen(f)} title={`${f.name} 내려받기`}
            style={{ border: 'none', background: 'transparent', padding: 0, fontFamily: 'inherit', fontSize: 12, color: '#1D4ED8', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>{f.name}</button>
          <span style={{ color: '#9CA3AF', fontSize: 11 }}>{size(f.size)}</span>
          {editable && (
            <button onClick={() => onRemove(f)} aria-label={`${f.name} 빼기`} title="빼기"
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#9CA3AF', display: 'inline-flex', padding: 2, borderRadius: 4 }}>
              <DismissRegular style={{ fontSize: 12 }} />
            </button>
          )}
        </span>
      ))}
      {editable && (
        <label className="dx-btn dx-btn-ghost" style={{ padding: '3px 10px', fontSize: 12, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
          <AttachRegular aria-hidden style={{ fontSize: 14 }} />{busy ? '올리는 중…' : '파일 첨부'}
          <input type="file" hidden multiple disabled={busy} onChange={(e) => { if (e.target.files?.length) onAdd(Array.from(e.target.files)); e.target.value = ''; }} />
        </label>
      )}
    </div>
  );
}

/** 회의 일시 변경(관리자) — 바꾸면 이번 회의 마감과 다음 주 작성 시작이 함께 바뀜 */
function MeetingModal({ weekKey, current, onClose }: { weekKey: string; current: string; onClose: () => void }): JSX.Element {
  const { flash } = useApp();
  const qc = useQueryClient();
  const [v, setV] = useState(toLocalInput(current));
  const [busy, setBusy] = useState(false);
  const apply = async (at: string | null) => {
    setBusy(true);
    try {
      await weeklyApi.setMeeting(weekKey, at);
      flash(at ? '회의 일시를 변경했습니다' : '기본 일시(월 10:00)로 되돌렸습니다');
      await qc.invalidateQueries({ queryKey: ['weekly'] });
      onClose();
    } catch (e) { flash(e instanceof Error ? e.message : '변경 실패'); } finally { setBusy(false); }
  };
  const nextOpen = v ? fmtShort(new Date(Math.max(Date.parse(`${v.slice(0, 10)}T13:00:00+09:00`), Date.parse(fromLocalInput(v)) + 3600000)).toISOString()) : '';
  return (
    <ModalFrame title="회의 일시 변경" sub={`${weekNo(weekKey)}주차 · 현재 ${fmtFull(current)}`} width={460} onClose={onClose}
      footer={<>
        <button className="dx-btn dx-btn-ghost" disabled={busy} onClick={() => void apply(null)}>기본값(월 10:00)</button>
        <span style={{ flex: 1 }} />
        <button className="dx-btn dx-btn-ghost" onClick={onClose}>취소</button>
        <button className="dx-btn dx-btn-primary" disabled={busy || !v} onClick={() => void apply(fromLocalInput(v))}>{busy ? '저장 중…' : '저장'}</button>
      </>}>
      <label className="dx-field-label" htmlFor="weekly-meeting-at">회의 일시</label>
      <input id="weekly-meeting-at" type="datetime-local" className="dx-input" value={v} onChange={(e) => setV(e.target.value)}
        style={{ width: '100%', boxSizing: 'border-box', height: 40, fontSize: 14 }} />
      <div className="dx-note" style={{ marginTop: 14 }}>
        <div>• 회의 시작 시각에 <b>이번 주 작성이 마감</b>됩니다.</div>
        <div>• 회의 당일 오후 13:00(회의가 더 늦으면 시작 1시간 뒤)부터 <b>다음 주 작성</b>이 열립니다.{nextOpen && <> → <b>{nextOpen}</b></>}</div>
      </div>
    </ModalFrame>
  );
}

const ROLE_ORDER: WeeklyRole[] = ['reporter', 'receiver', 'none'];
const ROLE_COLOR: Record<WeeklyRole, [string, string]> = { reporter: ['#EFF4FE', '#1D4ED8'], receiver: ['#FEF3C7', '#B45309'], none: ['#F3F4F6', '#6B7280'] };

/** 임직원 구분(보고자·피보고자·비보고자)·표시 순서 설정(관리자) */
function OrderModal({ onClose }: { onClose: () => void }): JSX.Element {
  const { flash } = useApp();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['weekly-config'], queryFn: () => weeklyApi.config() });
  const [list, setList] = useState<Array<WeeklyPerson & { role: WeeklyRole }> | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!q.data || list) return;
    const { config, employees } = q.data;
    const lc = (s: string) => s.toLowerCase();
    const rank = new Map(config.order.map((u, i) => [lc(u), i]));
    const recv = new Set((config.receivers ?? []).map(lc));
    const off = new Set(config.excluded.map(lc));
    const roleOf = (u: string): WeeklyRole => (recv.has(lc(u)) ? 'receiver' : off.has(lc(u)) ? 'none' : 'reporter');
    setList(employees
      .map((e) => ({ ...e, role: roleOf(e.upn) }))
      .sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role) || (rank.get(lc(a.upn)) ?? 1e9) - (rank.get(lc(b.upn)) ?? 1e9) || a.name.localeCompare(b.name, 'ko')));
  }, [q.data, list]);

  // 같은 구분 안에서만 위/아래 이동(구분이 바뀌면 그 구분의 맨 끝으로)
  const move = (i: number, dir: -1 | 1) => setList((l) => {
    if (!l) return l;
    const j = i + dir;
    if (j < 0 || j >= l.length || l[j].role !== l[i].role) return l;
    const n = [...l];
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });
  const setRole = (upn: string, role: WeeklyRole) => setList((l) => {
    if (!l) return l;
    const p = l.find((x) => x.upn === upn);
    if (!p || p.role === role) return l;
    const rest = l.filter((x) => x.upn !== upn);
    const updated = { ...p, role };
    const lastIdx = rest.reduce((acc, x, idx) => (ROLE_ORDER.indexOf(x.role) <= ROLE_ORDER.indexOf(role) ? idx : acc), -1);
    return [...rest.slice(0, lastIdx + 1), updated, ...rest.slice(lastIdx + 1)];
  });
  const save = async () => {
    if (!list) return;
    setBusy(true);
    try {
      await weeklyApi.saveConfig(list.map((p) => p.upn), list.filter((p) => p.role === 'none').map((p) => p.upn), list.filter((p) => p.role === 'receiver').map((p) => p.upn));
      flash('임직원 구분·표시 순서를 저장했습니다');
      await qc.invalidateQueries({ queryKey: ['weekly'] });
      onClose();
    } catch (e) { flash(e instanceof Error ? e.message : '저장 실패'); } finally { setBusy(false); }
  };
  const count = (r: WeeklyRole) => list?.filter((p) => p.role === r).length ?? 0;
  const arrow: CSSProperties = { width: 28, height: 28, border: '1px solid #E5E7EB', borderRadius: 7, background: '#fff', cursor: 'pointer', color: '#4B5563', fontSize: 12.5 };

  return (
    <ModalFrame title="임직원 구분·표시 순서" width={600} onClose={onClose}
      sub={<>보고자 = 주간보고 작성 · 피보고자 = 보고 받음(하단 업무진행/공지사항 작성) · 비보고자 = 예외. 같은 구분 안에서 위/아래로 순서를 정합니다.</>}
      footer={<>
        <span style={{ fontSize: 12.5, color: '#6B7280' }}>
          {ROLE_ORDER.map((r) => <span key={r} style={{ marginRight: 10 }}>{WEEKLY_ROLE_LABELS[r]} <b style={{ color: '#1F2937' }}>{count(r)}</b></span>)}
        </span>
        <span style={{ flex: 1 }} />
        <button className="dx-btn dx-btn-ghost" onClick={onClose}>취소</button>
        <button className="dx-btn dx-btn-primary" disabled={busy || !list} onClick={() => void save()}>{busy ? '저장 중…' : '저장'}</button>
      </>}>
      {!list ? <div style={{ padding: 24, textAlign: 'center' }}><Spinner size={22} /></div> : (
        <div style={{ border: '1px solid #EEF0F3', borderRadius: 12, overflow: 'hidden' }}>
          {list.map((p, i) => {
            const groupStart = i === 0 || list[i - 1].role !== p.role;
            const n = list.slice(0, i + 1).filter((x) => x.role === p.role).length;
            return (
              <div key={p.upn}>
                {groupStart && <div style={{ padding: '7px 12px', fontSize: 11.5, fontWeight: 800, color: ROLE_COLOR[p.role][1], background: ROLE_COLOR[p.role][0], borderTop: i ? '1px solid #EEF0F3' : 'none' }}>{WEEKLY_ROLE_LABELS[p.role]} {count(p.role)}명</div>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderTop: groupStart ? 'none' : '1px solid #F3F4F6', flexWrap: 'wrap' }}>
                  <span style={{ width: 20, fontSize: 11.5, color: '#9CA3AF', textAlign: 'right' }}>{n}</span>
                  <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>{p.name}</div>
                    <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>{[p.dept, p.title].filter(Boolean).join(' · ')}</div>
                  </div>
                  <span role="radiogroup" aria-label={`${p.name} 구분`} style={{ display: 'inline-flex', background: '#F3F4F6', borderRadius: 8, padding: 2 }}>
                    {ROLE_ORDER.map((r) => {
                      const on = p.role === r;
                      return (
                        <button key={r} role="radio" aria-checked={on} onClick={() => setRole(p.upn, r)}
                          style={{ border: 'none', borderRadius: 6, padding: '4px 9px', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer', background: on ? '#fff' : 'transparent', color: on ? ROLE_COLOR[r][1] : '#6B7280', boxShadow: on ? '0 1px 2px rgba(16,24,40,.12)' : 'none' }}>
                          {WEEKLY_ROLE_LABELS[r]}
                        </button>
                      );
                    })}
                  </span>
                  <button style={{ ...arrow, opacity: groupStart ? 0.35 : 1 }} disabled={groupStart} aria-label={`${p.name} 위로`} onClick={() => move(i, -1)}><ArrowUpRegular aria-hidden style={{ fontSize: 14 }} /></button>
                  <button style={{ ...arrow, opacity: i === list.length - 1 || list[i + 1].role !== p.role ? 0.35 : 1 }} disabled={i === list.length - 1 || list[i + 1].role !== p.role} aria-label={`${p.name} 아래로`} onClick={() => move(i, 1)}><ArrowDownRegular aria-hidden style={{ fontSize: 14 }} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </ModalFrame>
  );
}
